import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { avatarColors, avatarInitials } from '@/mocks/testimonials';

interface Testimonial {
  name: string;
  role: string;
  text: string;
  service: string;
}

export default function TestimonialsSection() {
  const { t } = useTranslation();
  const items = t('testimonials.items', { returnObjects: true }) as Testimonial[];

  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const visibleCount = isMobile ? 1 : 3;
  const totalSlides = Math.max(1, items.length - visibleCount + 1);

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % totalSlides);
  };

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <p className="text-champagne-500 text-xs font-medium tracking-[0.2em] uppercase mb-3">
            {t('testimonials.title')}
          </p>
          <h2 className="font-display text-2xl md:text-3xl lg:text-4xl font-bold text-dark-950 mb-3">
            {t('testimonials.title')}
          </h2>
          <p className="text-dark-500 text-base md:text-lg max-w-xl mx-auto">
            {t('testimonials.subtitle')}
          </p>

          <div className="inline-flex items-center gap-2 mt-4 bg-champagne-50 px-4 py-2 rounded-full border border-champagne-200">
            <div className="flex items-center gap-0.5">
              {[1, 2, 3, 4, 5].map((star) => (
                <i key={star} className="ri-star-fill text-amber-400 text-sm" />
              ))}
            </div>
            <span className="text-dark-800 font-bold text-sm">{t('testimonials.stars')}</span>
            <span className="text-dark-400 text-xs">· Google Reviews</span>
          </div>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{
                transform: `translateX(-${activeIndex * (100 / visibleCount)}%)`,
              }}
            >
              {items.map((item, idx) => (
                <div
                  key={idx}
                  className="w-full md:w-1/3 flex-shrink-0 px-3"
                >
                  <div className="bg-dark-950 rounded-2xl p-6 md:p-8 h-full flex flex-col border border-dark-800/50 hover:border-champagne-500/30 transition-colors">
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-4">
                      <div
                        className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${avatarColors[idx]}`}
                      >
                        {avatarInitials[idx]}
                      </div>
                      <div className="min-w-0">
                        <h4 className="text-white font-semibold text-sm truncate">
                          {item.name}
                        </h4>
                        <p className="text-white/40 text-xs">{item.role}</p>
                      </div>
                    </div>

                    {/* Stars */}
                    <div className="flex items-center gap-0.5 mb-3">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i key={s} className="ri-star-fill text-amber-400 text-xs" />
                      ))}
                      <span className="text-amber-400 text-xs font-semibold ml-1">
                        {t('testimonials.stars')}
                      </span>
                    </div>

                    {/* Quote */}
                    <div className="flex items-start gap-2 mb-4 flex-1">
                      <i className="ri-double-quotes-l text-champagne-400/30 text-xl flex-shrink-0 mt-1" />
                      <p className="text-white/80 text-sm leading-relaxed line-clamp-5">
                        {item.text}
                      </p>
                    </div>

                    {/* Service tag + verified */}
                    <div className="flex items-center justify-between pt-4 border-t border-white/10">
                      <span className="text-champagne-400 text-xs font-medium bg-champagne-500/10 px-2.5 py-1 rounded-full">
                        {item.service}
                      </span>
                      <span className="text-white/30 text-xs flex items-center gap-1">
                        <i className="ri-verified-badge-line" />
                        {t('testimonials.verified')}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Arrows */}
          <button
            onClick={goPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 md:-translate-x-4 w-10 h-10 rounded-full bg-white border border-dark-200 flex items-center justify-center text-dark-600 hover:text-dark-950 hover:border-dark-300 transition-colors shadow-sm cursor-pointer z-10"
            aria-label="Previous"
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>
          <button
            onClick={goNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 md:translate-x-4 w-10 h-10 rounded-full bg-white border border-dark-200 flex items-center justify-center text-dark-600 hover:text-dark-950 hover:border-dark-300 transition-colors shadow-sm cursor-pointer z-10"
            aria-label="Next"
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-6">
          {Array.from({ length: totalSlides }).map((_, i) => (
            <button
              key={i}
              onClick={() => setActiveIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                i === activeIndex
                  ? 'w-6 bg-champagne-500'
                  : 'w-2 bg-dark-200 hover:bg-dark-300'
              }`}
              aria-label={`Slide ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}